import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { PackService } from '../../services/api/pack.service';
import { CampoProductoValor, CampoProductoVC, DescuentosVcIn, EstadoDescuentosVC, Pack, PackConfig, PackProducto, PackProyecto, PackProyectoConfig, ProyectoProductoVC, TarificacionRapidaIn, TipoTarificacionProyectoVc } from '../../model/pack.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CaFormFieldComponent, CaIcon } from '@global-front-components/ui/form-field';
import { CaButtonComponent } from '@global-front-components/ui/button';
import { CatalogoSvgApiService } from '../../services/api/catalogo-svg-api.service';
import { PreguntaVentaCruzadaComponent } from '../pregunta-venta-cruzada/pregunta-venta-cruzada.component';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ProyectoVentaCruzadaComponent } from '../proyecto-venta-cruzada/proyecto-venta-cruzada.component';
import { CaCardComponent } from '@global-front-components/ui/card';
import { concatMap, from } from 'rxjs';
import { GlobalMessageHandlerService } from '../../services/util/global-message-handler.service';

@Component({
  selector: 'app-paquetes-venta-cruzada',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CaIcon,
    CaButtonComponent,
    PreguntaVentaCruzadaComponent,
    CaFormFieldComponent,
    ReactiveFormsModule,
    ProyectoVentaCruzadaComponent,
    CaCardComponent
  ],
  templateUrl: './paquetes-venta-cruzada.component.html',
  styleUrls: ['./paquetes-venta-cruzada.component.scss']
})
export class PaquetesVentaCruzadaComponent implements OnInit {
  @Input() idPack?: number;

  pack: Pack;
  productosDisponibles: ProyectoProductoVC[] = [];
  productosSeleccionados: ProyectoProductoVC[] = [];
  proyectoProductoPrincipal: ProyectoProductoVC;
  formMap: Record<string, FormGroup> = {};
  estadoDescuentos: EstadoDescuentosVC = EstadoDescuentosVC.SIN_DESCUENTOS;
  existenDescuentos: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private packService: PackService,
    private catalogoService: CatalogoSvgApiService,
    private translateService: TranslateService,
    private fb: FormBuilder,
    private msg: GlobalMessageHandlerService
  ) { }

  ngOnInit(): void {
    if (!this.idPack) {
      const idParam = this.route.snapshot.paramMap.get('idPack');
      this.idPack = idParam ? Number(idParam) : null;
    }

    if (this.idPack) {
      this.recuperarInfoPack();
    }
  }

  // --- FLUJO PRINCIPAL DE CARGA ---
  private recuperarInfoPack(): void {
    this.packService.consultarPack(this.idPack!).subscribe({
      next: (pack) => {
        this.pack = pack;
        const proyectoPrincipal = pack.proyectos?.find(p => p.esPrincipal && !p.esDescuento);

        if (!proyectoPrincipal) {
          this.msg.emitError('No se ha encontrado proyecto principal');
          return;
        }

        this.cargarConfiguraciones(pack, proyectoPrincipal);
      },
      error: (err) => console.error('Error al consultar pack', err)
    });
  }

  private cargarConfiguraciones(pack: Pack, principal: PackProyecto): void {
    // Preparar input exacto para la consulta de configs
    const packConfigInput: PackProducto = {
      idPack: this.idPack,
      codCia: principal.codCia,
      codRamo: principal.codRamo,
      codModa: principal.codModa,
      codSubmoda: principal.codSubmoda,
      codColectiva: principal.codColectiva,
      codProductoVc: principal.codProductoVc
    };

    this.packService.consultarConfigsPacks(packConfigInput).subscribe({
      next: (config: PackConfig) => {
        const configs = config?.configsProyectos || [];
        this.inicializarListas(pack, configs, principal);
        this.cargarDescuentos(pack);
      },
      error: (err) => console.error('Error al consultar configs de packs', err)
    });
  }

  private inicializarListas(pack: Pack, configs: PackProyectoConfig[], principal: PackProyecto): void {
    // 1. Principal
    const configPrincipal = configs.find(c => c.codProductoVc === principal.codProductoVc);
    const proyDescuentoPrincipal = pack.proyectos?.find(p => p.esPrincipal && p.esDescuento) || null;

    this.proyectoProductoPrincipal = this.crearProyectoProductoVC(principal, configPrincipal, proyDescuentoPrincipal);
    this.productosSeleccionados = [this.proyectoProductoPrincipal];
    this.productosDisponibles = [];

    // 2. Clasificar proyectos existentes en el pack
    let proyectosNoPrincipal = [...(pack.proyectos?.filter(p => !p.esPrincipal) || [])];
    const proysDescuento = proyectosNoPrincipal.filter(p => p.esDescuento);
    proyectosNoPrincipal = proyectosNoPrincipal.filter(p => !p.esDescuento);

    proyectosNoPrincipal.forEach(proy => {
      const configProy = configs.find(c => c.codProductoVc === proy.codProductoVc && (
        (c.tipoTarificacionProyectoVc === TipoTarificacionProyectoVc.COLECTIVA ? c.codColectiva === proy.codColectiva : true)));

      if (configProy && configProy.tipoTarificacionProyectoVc === TipoTarificacionProyectoVc.COLECTIVA) {
        configProy.codColectiva = proy.codColectiva;
      }
      //Ordenamos enlaces de haber
      if (configProy?.enlaces?.length > 0) {
        configProy.enlaces = [...configProy.enlaces].sort((a, b) => a.numOrden - b.numOrden);
      }
      const proyectoDescuento = proysDescuento.find(d => d.codProductoVc === proy.codProductoVc && d.codColectiva === proy.codColectiva);
      const ppVc = this.crearProyectoProductoVC(proy, configProy, proyectoDescuento);

      proy.esSeleccionado || proyectoDescuento?.esSeleccionado
        ? this.productosSeleccionados.push(ppVc)
        : this.productosDisponibles.push(ppVc);
    });

    // 3. Añadir sugerencias (configs que no están en el pack)
    configs.forEach(conf => {
      const yaEnPack = pack.proyectos?.some(p => p.codProductoVc === conf.codProductoVc);
      if (!yaEnPack && conf.codProductoVc !== principal.codProductoVc) {
        this.productosDisponibles.push({
          proyectoVC: null,
          titulo: this.getTituloProductoVC(null, conf.codProductoVc),
          mensaje: this.getMensajeProductoVC(null, conf.codProductoVc),
          configProyecto: conf
        });
      }
    });
  }

  // --- ACCIONES DE PRODUCTO (AÑADIR / SIMULAR) ---

  onAnadirProductoVC(pvc: ProyectoProductoVC): void {
    if (!this.idPack || !pvc.configProyecto.codProductoVc) return;

    const tipo = pvc.configProyecto.tipoTarificacionProyectoVc;
    switch (tipo) {
      case TipoTarificacionProyectoVc.SVG:
        this._anadirCamposPorCodProducto(pvc);
        break;
      case TipoTarificacionProyectoVc.COLECTIVA:
        this._anadirPorColectiva(pvc);
        break;
      case TipoTarificacionProyectoVc.VIDA:
        this._anadirCamposVida(pvc);
        break;
      case TipoTarificacionProyectoVc.PRECIO_FIJO:
        this._anadirPorPrecioFijo(pvc);
        break;
      default:
        console.warn('Tipo de tarificación no gestionado:', tipo);
    }
  }

  private _anadirCamposPorCodProducto(pvc: ProyectoProductoVC): void {
    const config = pvc.configProyecto;

    this.packService.consultarCamposProductoVC(
      config.codCia, config.codRamo, config.codModa, config.codSubmoda,
      this.proyectoProductoPrincipal.proyectoVC.idProyecto
    ).subscribe({
      next: (campos: CampoProductoVC[]) => {

        // Si no hay campos, tarificar directamente
        if (!campos || campos.length === 0) {
          this.onSimularProductoVC(pvc);
          return;
        }

        this.packService.consultarColectiva(config.codProductoVc).subscribe({
          next: (colectiva) => {
            this.catalogoService.getCatalogoColectiva(colectiva).subscribe({
              next: (catalogo) => {
                // Vincular preguntas al campo
                pvc.configProyecto.camposProducto = campos.map(campo => ({
                  ...campo,
                  preguntaCatalogo: catalogo?.preguntas?.find(p => 'preg_' + p.idPregunta === campo.codCampoDestino) || null
                }));
                const hayVisibles = pvc.configProyecto.camposProducto?.some(c => c.visible === true) === true;

                if (hayVisibles) {
                  this.crearFormParaProducto(config.codProductoVc);
                  this.enviarEventoNuevoProyectoPack(pvc);
                } else {
                  this.onSimularProductoVC(pvc);
                }
              }
            });
          }
        });
      }
    });
  }

  onSimularProductoVC(pvc: ProyectoProductoVC): void {
    const form = this.formMap[pvc.configProyecto.codProductoVc];
    const campos = this.extraerValoresDelFormulario(pvc, form);

    const input: TarificacionRapidaIn = {
      idPack: this.pack.idPack,
      codCia: pvc.configProyecto.codCia,
      codRamo: pvc.configProyecto.codRamo,
      codModa: pvc.configProyecto.codModa,
      codSubmoda: pvc.configProyecto.codSubmoda,
      codColectiva: pvc.configProyecto.codColectiva,
      listaCampos: campos
    };

    this.packService.tarificacionRapida(input).subscribe({
      next: (recibo) => {
        if (!recibo) return;

        const nuevoProyIn = this.crearDTOProyectoBase(pvc);
        nuevoProyIn.impPrimaPrimerRec = recibo.reciboPrimerPago?.importeTotal || 0;
        nuevoProyIn.impPrimaTotal = recibo.reciboAnualidad?.importeTotal || recibo.reciboPrimerPago?.importeTotal || 0;
        nuevoProyIn.impPrimaRecSucesivos = recibo.reciboPagosSucesivos?.importeTotal || 0;

        this.packService.anadirProyecto(this.idPack, nuevoProyIn).subscribe({
          next: (proyecto) => {
            pvc.proyectoVC = proyecto;
            this.moverASeleccionados(pvc);
            this._actualizarEstadoDescuentos();
          }
        });
      }
    });
  }

  private _anadirPorPrecioFijo(pvc: ProyectoProductoVC): void {
    const nuevoProyIn = this.crearDTOProyectoBase(pvc);
    this.packService.anadirProyecto(this.idPack, nuevoProyIn).subscribe({
      next: (proyecto) => {
        pvc.proyectoVC = proyecto;
        this.moverASeleccionados(pvc);
        this._actualizarEstadoDescuentos();
      }
    });
  }

  private _anadirPorColectiva(pvc: ProyectoProductoVC): void {
    const proyPrincipal = this.proyectoProductoPrincipal.proyectoVC;
    this.packService.retarificarPropuestaColectiva(proyPrincipal.codProyecto, pvc.configProyecto.codColectiva).subscribe({
      next: (propuesta) => {
        if (!propuesta.recibo) return;

        const nuevoProyIn: PackProyecto = {
          codProductoVc: pvc.configProyecto.codProductoVc,
          codProyecto: String(propuesta.codigoPropuesta),
          codCia: String(propuesta.codigoCompannia),
          codRamo: propuesta.codigoRamo,
          codModa: propuesta.codigoModalidad,
          codSubmoda: propuesta.codigoSubmodalidad,
          codColectiva: propuesta.codColectiva,
          impPrimaPrimerRec: propuesta.recibo.reciboPrimerPago?.importeTotal,
          impPrimaTotal: propuesta.recibo.reciboAnualidad?.importeTotal,
          impPrimaRecSucesivos: propuesta.recibo.reciboPagosSucesivos?.importeTotal,
          esSeleccionado: false,
          esPrincipal: false,
          esDescuento: false,
        };

        this.packService.anadirProyecto(this.idPack, nuevoProyIn).subscribe({
          next: (proyecto) => pvc.proyectoVC = proyecto
        });
      }
    });
  }

  // --- SELECCIÓN / DESELECCIÓN ---

  onSeleccionarProductoVC(pvc: ProyectoProductoVC, seleccionado: boolean): void {
    if (!this.idPack || !pvc.configProyecto.codProductoVc) return;
    const actualmenteSeleccionado = pvc.proyectoVC?.esSeleccionado === true || pvc.proyectoDescuentoVC?.esSeleccionado === true;
    if (actualmenteSeleccionado === seleccionado) return;

    if (seleccionado) {
      this._seleccionarProducto(pvc);
    } else {
      this._desseleccionarProducto(pvc);
    }
  }

  private _seleccionarProducto(pvc: ProyectoProductoVC): void {
    if (this.productosSeleccionados.includes(pvc)) return;

    const objetivoEsDescuento = this.existenDescuentos && !!pvc.proyectoDescuentoVC?.idProyecto;

    if ((objetivoEsDescuento && pvc.proyectoDescuentoVC.impPrimaTotal === 0) ||
      (!objetivoEsDescuento && pvc.proyectoVC.impPrimaTotal === 0)
    ){
      if (pvc.proyectoDescuentoVC?.impPrimaTotal === 0){
        this.msg.emitError(this.translateService.instant('PACKS.ERROR.NO_PRIMA'));
        return;
      }
    } 

    const idSeleccionar = objetivoEsDescuento
      ? pvc.proyectoDescuentoVC!.idProyecto
      : pvc.proyectoVC?.idProyecto;

    if (!idSeleccionar) return;

    const idNoSeleccionar =
      objetivoEsDescuento ? pvc.proyectoVC?.idProyecto : pvc.proyectoDescuentoVC?.idProyecto;

    const ops: Array<{ id: number; sel: boolean }> = [];
    if (idNoSeleccionar && idNoSeleccionar !== idSeleccionar) ops.push({ id: idNoSeleccionar, sel: false });
    ops.push({ id: idSeleccionar, sel: true });

    from(ops)
      .pipe(concatMap(op => this.packService.actualizarSeleccionProyecto(op.id, op.sel)))
      .subscribe({
        next: () => { },
        complete: () => {
          if (pvc.proyectoVC) pvc.proyectoVC.esSeleccionado = !objetivoEsDescuento;
          if (pvc.proyectoDescuentoVC) pvc.proyectoDescuentoVC.esSeleccionado = objetivoEsDescuento;

          this.moverASeleccionados(pvc);
          this.crearFormParaProducto(pvc.configProyecto.codProductoVc);
          this._actualizarEstadoDescuentos();
          this._sincronizarPrincipalPorComplementarios();
          this.enviarEventoNuevoProyectoPack(pvc);
        }
      });
  }

  private _desseleccionarProducto(pvc: ProyectoProductoVC): void {
    const idSeleccionadoActual =
      pvc.proyectoVC?.esSeleccionado ? pvc.proyectoVC.idProyecto :
        pvc.proyectoDescuentoVC?.esSeleccionado ? pvc.proyectoDescuentoVC.idProyecto :
          pvc.proyectoVC?.idProyecto || pvc.proyectoDescuentoVC?.idProyecto;

    if (!idSeleccionadoActual) return;

    this.packService.actualizarSeleccionProyecto(idSeleccionadoActual, false).subscribe({
      next: () => { },
      complete: () => {
        if (pvc.proyectoVC && pvc.proyectoVC.idProyecto === idSeleccionadoActual) {
          pvc.proyectoVC.esSeleccionado = false;
        }
        if (pvc.proyectoDescuentoVC && pvc.proyectoDescuentoVC.idProyecto === idSeleccionadoActual) {
          pvc.proyectoDescuentoVC.esSeleccionado = false;
        }

        this.moverADisponibles(pvc);
        if (pvc.configProyecto.codProductoVc) delete this.formMap[pvc.configProyecto.codProductoVc];
        this._actualizarEstadoDescuentos();
        this._sincronizarPrincipalPorComplementarios();
        this.enviarEventoEliminarProyectoPack(pvc);
      }
    });
  }

  // --- DESCUENTOS ---
  private cargarDescuentos(pack: Pack): void {
    if (!pack.codPaquete) return;

    const listaConfigs = [...this.productosSeleccionados, ...this.productosDisponibles]
      .map(p => p.configProyecto)
      .filter(c => !!c);

    const input: DescuentosVcIn = {
      idPack: pack.idPack!,
      listaProyectosConfig: listaConfigs
    };

    this.packService.consultarDescuentosVC(input).subscribe({
      next: (res) => {
        this.productosDisponibles.forEach(p => delete p.descuento);
        this.productosSeleccionados.forEach(p => delete p.descuento);

        if (res?.descuentosVc?.length) {

          res.descuentosVc.forEach(dto => {
            const p =
              this.productosDisponibles.find(x =>
                x.configProyecto.codProductoVc === dto.codProductoVc &&
                x.configProyecto.codColectiva === dto.codColectiva
              ) ||
              this.productosSeleccionados.find(x =>
                x.configProyecto.codProductoVc === dto.codProductoVc &&
                x.configProyecto.codColectiva === dto.codColectiva
              );

            if (p) {
              p.descuento = dto;
              this.existenDescuentos = true;
            }
          });

          this._actualizarEstadoDescuentos();
        }
      }
    });
  }

  private _actualizarEstadoDescuentos(): void {
    if (!this.existenDescuentos) {
      this.estadoDescuentos = EstadoDescuentosVC.SIN_DESCUENTOS;
      return;
    }
    const hayExtra = this.productosSeleccionados.some(p => p.proyectoVC && !p.proyectoVC.esPrincipal && (p.proyectoVC.esSeleccionado || p.proyectoDescuentoVC.esSeleccionado) && p.descuento);
    this.estadoDescuentos = hayExtra ? EstadoDescuentosVC.DISPONIBLES_CON_PROYECTO : EstadoDescuentosVC.DISPONIBLES_SIN_PROYECTO;
  }

  // --- HELPERS ---

  private crearProyectoProductoVC(proy: PackProyecto, config: PackProyectoConfig, descuento?: PackProyecto): ProyectoProductoVC {
    return {
      proyectoVC: proy,
      proyectoDescuentoVC: descuento,
      titulo: this.getTituloProductoVC(proy, proy.codProductoVc),
      mensaje: this.getMensajeProductoVC(proy, proy.codProductoVc),
      configProyecto: config
    };
  }

  private crearDTOProyectoBase(pvc: ProyectoProductoVC): PackProyecto {
    return {
      codProductoVc: pvc.configProyecto.codProductoVc,
      codCia: pvc.configProyecto.codCia,
      codRamo: pvc.configProyecto.codRamo,
      codModa: pvc.configProyecto.codModa,
      codSubmoda: pvc.configProyecto.codSubmoda,
      codColectiva: pvc.configProyecto.codColectiva || '0',
      impPrimaPrimerRec: 0,
      impPrimaTotal: 0,
      impPrimaRecSucesivos: 0,
      esSeleccionado: true,
      esPrincipal: false,
      esDescuento: false,
    };
  }

  private moverASeleccionados(pvc: ProyectoProductoVC): void {
    const idx = this.productosDisponibles.indexOf(pvc);
    if (idx !== -1) this.productosDisponibles.splice(idx, 1);
    if (!this.productosSeleccionados.includes(pvc)) this.productosSeleccionados.push(pvc);
    this.enviarEventoNuevoProyectoPack(pvc);
  }

  private enviarEventoNuevoProyectoPack(pvc: ProyectoProductoVC) {
    this.enviarEvento('nuevoProyectoPack',
      {
      idPack: this.idPack!, codProductoVentaCruzada: pvc.configProyecto.codProductoVc, colectiva: pvc.configProyecto.codColectiva
      });
  }

  private enviarEventoEliminarProyectoPack(pvc: ProyectoProductoVC) {
    this.enviarEvento('eliminarProyectoPack', { idPack: this.idPack!, codProductoVentaCruzada: pvc.configProyecto.codProductoVc!, colectiva: pvc.configProyecto.codColectiva});
  }

  private moverADisponibles(pvc: ProyectoProductoVC): void {
    const idx = this.productosSeleccionados.indexOf(pvc);
    if (idx !== -1) this.productosSeleccionados.splice(idx, 1);
    if (!this.productosDisponibles.includes(pvc)) this.productosDisponibles.push(pvc);
    this.enviarEventoEliminarProyectoPack(pvc);
  }

  private extraerValoresDelFormulario(pvc: ProyectoProductoVC, form: FormGroup): CampoProductoValor[] {
    const valores: CampoProductoValor[] = [];
    if (pvc.configProyecto?.camposProducto && form) {
      pvc.configProyecto.camposProducto.forEach(c => {
        const val = form.get(c.codCampo)?.value;
        if (val !== null && val !== undefined) {
          valores.push({ codCampo: c.codCampo, valor: val.toString() });
        }
      });
    }
    return valores;
  }

  getTituloProductoVC(proyecto: PackProyecto, codigoVC: string): string {
    if (proyecto) {
      if (proyecto.txtProyecto) return proyecto.txtProyecto;
      const rms = `${proyecto.codRamo}${proyecto.codModa}${proyecto.codSubmoda}`;
      const keys = [
        `PACKS.PRODUCTOS.${rms}.COLECTIVAS.${proyecto.codColectiva}`,
        `PACKS.PRODUCTOS.COD_PRODUCTO_VC.${codigoVC || proyecto.codProductoVc}.TITULO`,
        `PACKS.PRODUCTOS.${rms}.TITULO`
      ];
      for (const key of keys) {
        const t = this.translateService.instant(key);
        if (t !== key) return t;
      }
    }
    const fallbackKey = `PACKS.PRODUCTOS.COD_PRODUCTO_VC.${codigoVC}.TITULO`;
    const fallbackRes = this.translateService.instant(fallbackKey);
    return fallbackRes !== fallbackKey ? fallbackRes : (codigoVC || '');
  }

  getMensajeProductoVC(proyecto: PackProyecto, codigoVC: string): string {
    const keys = [];
    if (proyecto?.txtProyecto) {
      keys.push('PACKS.PRODUCTOS.COD_PRODUCTO_VC.' + codigoVC + '.' + proyecto.txtProyecto.trim().replace(' ', '_').toUpperCase() + '.DESCRIPCION');
    }
    keys.push('PACKS.PRODUCTOS.COD_PRODUCTO_VC.' + codigoVC + '.DESCRIPCION');
    for (const key of keys) {
      const t = this.translateService.instant(key);
      if (t !== key) return t;
    }
    return null;
  }

  private crearFormParaProducto(cod: string) {
    if (cod) this.formMap[cod] = this.fb.group({});
  }

  onModificarProductoVC(pvc: ProyectoProductoVC): void {
    if (this.idPack && pvc.proyectoVC) {
      this.enviarEvento('modificarProyectoPack', {
        idPack: this.idPack,
        codProductoVentaCruzada: pvc.configProyecto.codProductoVc!,
        codProyecto: pvc.proyectoVC.codProyecto
      });
    }
  }

  private _anadirCamposVida(pvc: ProyectoProductoVC) {
    console.log('🚧 VIDA no implementado:', pvc.configProyecto.codProductoVc);
  }

  private enviarEvento(tipo: string, data: any): void {
    window.parent.postMessage({ tipo, data }, '*');
  }

  private _numComplementariosSeleccionados(): number {
    return this.productosSeleccionados.filter(p => !p.proyectoVC?.esPrincipal).length;
  }

  private _sincronizarPrincipalPorComplementarios(): void {
    if (!this.existenDescuentos) return;
    const principal = this.proyectoProductoPrincipal;
    if (!principal?.proyectoVC?.idProyecto || !principal?.proyectoDescuentoVC?.idProyecto) return;

    const hayComplementarios = this._numComplementariosSeleccionados() > 0;
    const selNormal = principal.proyectoVC.esSeleccionado === true;
    const selDesc = principal.proyectoDescuentoVC.esSeleccionado === true;

    const ops: Array<{ id: number; sel: boolean }> = [];
    if (hayComplementarios) {
      if (!selDesc) ops.push({ id: principal.proyectoDescuentoVC.idProyecto, sel: true });
      if (selNormal) ops.push({ id: principal.proyectoVC.idProyecto, sel: false });
    } else {
      if (!selNormal) ops.push({ id: principal.proyectoVC.idProyecto, sel: true });
      if (selDesc) ops.push({ id: principal.proyectoDescuentoVC.idProyecto, sel: false });
    }
    if (!ops.length) return;

    from(ops)
      .pipe(concatMap(op => this.packService.actualizarSeleccionProyecto(op.id, op.sel)))
      .subscribe({
        next: () => { },
        complete: () => {
          principal.proyectoVC.esSeleccionado = !hayComplementarios;
          principal.proyectoDescuentoVC.esSeleccionado = hayComplementarios;
        }
      });
  }
}